import { useExperience, useNinetailed } from '@ninetailed/experience.js-next';
import { useNinetailedAbTestContext } from 'contexts/ninetailed-ab-test';
import { IDiscount, IDiscountFields, IGlobalData } from 'global';
import React, { useEffect } from 'react';
import { usePrevious } from 'react-use';
import { MyVariant, isVariantRef, parseExperiences } from 'utils/ninetailed';

type Discount = MyVariant<IDiscountFields>;

export interface GlobalDiscount {
  loading: boolean;
  discount: null | Discount;
}

/**
 * `globalData.globalDiscount` is a multi-reference that can hold both `discount`
 * and `dynamicBundleDiscount` entries. The personalization logic below operates
 * on the `discount` shape only, so pick the first `discount` entry from the array.
 */
const getDiscountEntry = (globalData: IGlobalData): IDiscount | undefined => {
  const entries = globalData?.fields.globalDiscount;
  if (!Array.isArray(entries)) {
    return undefined;
  }

  return entries.find((entry): entry is IDiscount => entry?.sys.contentType.sys.id === 'discount');
};

/**
 * Retrieve global discount configuration based on personalization.
 * @TODO : globalDiscount is updated too many time. Should track down the reason why
 */
const useGlobalDiscount = (globalData: IGlobalData): GlobalDiscount => {
  const { setToHeader, deleteHeader } = useNinetailedAbTestContext();
  const { track, trackComponentView } = useNinetailed();

  const discountEntry = React.useMemo(() => getDiscountEntry(globalData), [globalData]);

  const _baseline: Discount = React.useMemo(
    () => ({
      title: '',
      amountInPercent: 0,
      firstSubscriptionOrderDiscount: 0,
      ...discountEntry?.fields,
      id: discountEntry?.sys.id || '',
    }),
    [discountEntry]
  );

  const experiences = parseExperiences({
    entry: discountEntry,
  });

  const { variant, audience, error, experience, loading, variantIndex } = useExperience<
    Discount,
    {
      id: string;
    }
  >({
    baseline: _baseline,
    experiences,
  });

  const globalDiscount: GlobalDiscount = React.useMemo(() => {
    if (loading) {
      return {
        loading: true,
        discount: null,
      };
    }

    if (error || !discountEntry) {
      return {
        loading: false,
        discount: null,
      };
    }

    if (variant && !isVariantRef(variant)) {
      return {
        loading: false,
        discount: {
          // When no variant is not found, variant is null.
          // here we have to assign default
          ..._baseline,
          ...variant,
          id: variant.id,
        },
      };
    } else {
      return {
        loading: false,
        discount: {
          ..._baseline,
        },
      };
    }
  }, [_baseline, discountEntry, error, loading, variant]);

  const seenBaselineFired = React.useRef(false);

  /**
   * If the baseline was seen we need to send an event, in order to persist this discount after login for the sake of consistency.
   * This should be removed once our tracking data is propagated through our customer base.
   */
  React.useEffect(() => {
    if (
      !seenBaselineFired.current &&
      loading === false &&
      audience &&
      audience.id === 'baseline' &&
      variant
    ) {
      track('hasSeenBaselineDiscount', { discountId: variant.id });
      seenBaselineFired.current = true;
    }
  }, [audience, loading, variant, track]);

  /**
   * Track global discount
   * @TODO : Add more detailed explanation.
   */
  React.useEffect(() => {
    if (isVariantRef(variant)) {
      trackComponentView({
        element: null as any,
        experience,
        audience,
        variant,
        variantIndex,
      });
    }
  }, [audience, experience, globalData, loading, trackComponentView, variant, variantIndex]);

  const experienceId = experience?.id;

  /**
   * Set to header
   */
  useEffect(() => {
    if (variantIndex !== undefined && experienceId) {
      setToHeader({
        experienceId,
        variant: variantIndex,
      });
    }
  }, [experienceId, setToHeader, variantIndex]);

  const preExperienceId = usePrevious(experienceId);

  /**
   * Remove from header once user is not applicable any more from the experience
   */
  useEffect(() => {
    if (preExperienceId && !experienceId) {
      deleteHeader({ experienceId: preExperienceId });
    }
  }, [deleteHeader, experienceId, preExperienceId]);

  return globalDiscount;
};

export default useGlobalDiscount;
